import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DataTypeOptions } from '@core/constants/data-types.constants';
import { convert, CurrencyExchange } from '@core/models/currency-exchange';
import { InvestmentData } from '@core/models/investment.model';
import { Product } from '@core/models/product.model';
import { LanguageService } from '@i18n/services/language.service';

@Component({
  selector: 'mfp-product-feature-investment',
  templateUrl: './product-feature-investment.component.html',
  styleUrls: ['./product-feature-investment.component.scss']
})
export class ProductFeatureInvestmentComponent implements OnInit {
  @Input() product!: Product;
  @Input() investment!: InvestmentData;
  @Input() currencyExchange!: CurrencyExchange;
  @Input() selectPlaceholder? = '';
  @Input() selectValues?: number[] = [];
  @Input() disabled = true;
  @Output() investmentChange = new EventEmitter<InvestmentData>();

  values: DataTypeOptions<number> = [];

  constructor(private language: LanguageService) {}

  ngOnInit(): void {
    if (this.selectValues) {
      this.selectValues.forEach(value => {
        this.values.push({ key: `${value}%`, value: value });
      });
    }
  }

  getValue() {
    return this.getAmount();
  }

  getSelectValue() {
    if (this.investment.projection) {
      return this.investment.projection;
    }
    return -1;
  }

  getAmount() {
    if (this.investment.amount) {
      return Math.round(convert(this.investment.amount, this.product.selectedCurrency, this.currencyExchange).amount);
    }
    return 0;
  }

  onChangeActive(active: boolean) {
    this.investment.active = active;
    if (!this.investment.amount || !this.investment.projection) {
      this.investment.valid = false;
    }
    this.investmentChange.emit(this.investment);
  }

  onChangeAmount(event: string) {
    if (isNaN(+event)) {
      event = event.replace('%', '');
      if (isNaN(+event)) {
        return;
      }
      switch (this.product.type) {
        case 'normal':
          const currentAssuredAmount = convert(
            this.product.currentAssuredAmount,
            this.product.selectedCurrency,
            this.currencyExchange
          );
          event = (+event / 100) * currentAssuredAmount.amount + '';
          break;
        case 'investment':
          const amountAtEndOfTemporality = convert(
            this.product.amountAtEndOfTemporality,
            this.product.selectedCurrency,
            this.currencyExchange
          );
          event = (+event / 100) * amountAtEndOfTemporality.amount + '';
          break;
      }
    }
    const amount = +event;
    if (amount) {
      this.investment.amount = {
        currency: this.product.selectedCurrency,
        amount
      };
    } else {
      delete this.investment.amount;
    }

    if (this.getAmountError() !== '' || this.getSelectError() !== '') {
      this.investment.valid = false;
    } else {
      this.investment.valid = true;
    }

    this.investmentChange.emit(this.investment);
  }

  onChangeSelect(event: number) {
    if (this.selectValues?.includes(event)) {
      this.investment.projection = event;
    }

    if (this.getAmountError() !== '' || this.getSelectError() !== '') {
      this.investment.valid = false;
    } else {
      this.investment.valid = true;
    }

    this.investmentChange.emit(this.investment);
  }

  getAmountError(): string {
    if (!this.investment.amount) {
      return this.language.instant('PRODUCT_SELECT.MANDATORY_FIELD');
    }
    const amount = this.getAmount();
    if (amount <= 0) {
      return this.language.instant('PRODUCT_SELECT.INVESTMENT.ERROR.MIN', {
        min: 1
      });
    }
    return '';
  }

  getSelectError(): string {
    if (!this.investment.projection) {
      return this.language.instant('PRODUCT_SELECT.MANDATORY_FIELD');
    }
    return '';
  }
}
